/**
 * Commercial name: SEO-Presta
 * Proprietary-license whose copyright belongs to the Licensor PRESTA MODULE, a company registered under French law
 * under the number 528 531 767, located 90 rue Stanislas Torrents 13006 Marseille
 * support@presta-module.com https://www.presta-module.com
 *
 * SEO-Presta’ source codes are protected and Licensees are not allowed to create any Derivated Work.
 * The distribution or resale of SEO-Presta is strictly forbidden.
 *
 * SEO-Presta shall be used for Licensees’ own use, which allows Customization.
 *
 * Licensees shall not distribute nor resale publicly any Customization of SEO-Presta. However, Licensees are allowed
 * to sale a customization of SEO-Presta as part of a service provided to a designated customer.
 *
 * Limitation of Liability: Under no circumstances and under no legal theory, whether in tort (including negligence),
 * contract, or otherwise, shall the Licensor be liable to anyone for any indirect, special, incidental, or
 * consequential damages of any character arising as a result of this License including, without limitation, damages
 * for loss of goodwill, work stoppage, computer failure or malfunction, or any and all other commercial damages
 * or losses. This limitation of liability shall not apply to the extent applicable law prohibits such limitation.
 *
 * Attribution notice: SEO-Presta is a Derivated Work from an Original Work created by PrestaShop SA
 * and its contributors which can be found at https://github.com/PrestaShop/PrestaShop
 */
import LocalizationException from './exception/localization';

class NumberSymbol {
    /**
     * NumberSymbolList constructor.
     *
     * @param string decimal Decimal separator character
     * @param string group Digits group separator character
     * @param string list List elements separator character
     * @param string percentSign Percent sign character
     * @param string minusSign Minus sign character
     * @param string plusSign Plus sign character
     * @param string exponential Exponential character
     * @param string superscriptingExponent Superscripting exponent character
     * @param string perMille Permille sign character
     * @param string infinity The infinity sign. Corresponds to the IEEE infinity bit pattern.
     * @param string nan The NaN (Not A Number) sign. Corresponds to the IEEE NaN bit pattern.
     *
     * @throws LocalizationException
     */
    constructor(
        decimal,
        group,
        list,
        percentSign,
        minusSign,
        plusSign,
        exponential,
        superscriptingExponent,
        perMille,
        infinity,
        nan,
    ) {
        this.decimal = decimal;
        this.group = group;
        this.list = list;
        this.percentSign = percentSign;
        this.minusSign = minusSign;
        this.plusSign = plusSign;
        this.exponential = exponential;
        this.superscriptingExponent = superscriptingExponent;
        this.perMille = perMille;
        this.infinity = infinity;
        this.nan = nan;

        this.validateData();
    }

    /**
     * Get the decimal separator.
     *
     * @return string
     */
    getDecimal() {
        return this.decimal;
    }

    /**
     * Get the digit groups separator.
     *
     * @return string
     */
    getGroup() {
        return this.group;
    }

    /**
     * Get the list elements separator.
     *
     * @return string
     */
    getList() {
        return this.list;
    }

    /**
     * Get the percent sign.
     *
     * @return string
     */
    getPercentSign() {
        return this.percentSign;
    }

    /**
     * Get the minus sign.
     *
     * @return string
     */
    getMinusSign() {
        return this.minusSign;
    }

    /**
     * Get the plus sign.
     *
     * @return string
     */
    getPlusSign() {
        return this.plusSign;
    }

    /**
     * Get the exponential character.
     *
     * @return string
     */
    getExponential() {
        return this.exponential;
    }

    /**
     * Get the exponent character.
     *
     * @return string
     */
    getSuperscriptingExponent() {
        return this.superscriptingExponent;
    }

    /**
     * Gert the per mille symbol (often "‰").
     *
     * @see https://en.wikipedia.org/wiki/Per_mille
     *
     * @return string
     */
    getPerMille() {
        return this.perMille;
    }

    /**
     * Get the infinity symbol (often "∞").
     *
     * @see https://en.wikipedia.org/wiki/Infinity_symbol
     *
     * @return string
     */
    getInfinity() {
        return this.infinity;
    }

    /**
     * Get the NaN (not a number) sign.
     *
     * @return string
     */
    getNan() {
        return this.nan;
    }

    /**
     * Symbols list validation.
     *
     * @throws LocalizationException
     */
    validateData() {
        if (!this.decimal || typeof this.decimal !== 'string') {
            throw new LocalizationException('Invalid decimal');
        }

        if (!this.group || typeof this.group !== 'string') {
            throw new LocalizationException('Invalid group');
        }

        if (!this.list || typeof this.list !== 'string') {
            throw new LocalizationException('Invalid symbol list');
        }

        if (!this.percentSign || typeof this.percentSign !== 'string') {
            throw new LocalizationException('Invalid percentSign');
        }

        if (!this.minusSign || typeof this.minusSign !== 'string') {
            throw new LocalizationException('Invalid minusSign');
        }

        if (!this.plusSign || typeof this.plusSign !== 'string') {
            throw new LocalizationException('Invalid plusSign');
        }

        if (!this.exponential || typeof this.exponential !== 'string') {
            throw new LocalizationException('Invalid exponential');
        }

        if (!this.superscriptingExponent || typeof this.superscriptingExponent !== 'string') {
            throw new LocalizationException('Invalid superscriptingExponent');
        }

        if (!this.perMille || typeof this.perMille !== 'string') {
            throw new LocalizationException('Invalid perMille');
        }

        if (!this.infinity || typeof this.infinity !== 'string') {
            throw new LocalizationException('Invalid infinity');
        }

        if (!this.nan || typeof this.nan !== 'string') {
            throw new LocalizationException('Invalid nan');
        }
    }
}

export default NumberSymbol;
